import {
  AutocompleteCommand,
  SuggestCommand,
  SearchTextCommand,
  GetPlaceCommand,
  SuggestResultItem,
  AutocompleteResultItem,
  GetPlaceCommandOutput,
  SearchTextResultItem,
} from "@aws-sdk/client-geo-places";
import { getPlacesClient } from "./placesClient";

export type PlaceAddress = {
  placeId: string;
  label: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
};

export function mapV2PlaceToAddress(
  p: GetPlaceCommandOutput | SearchTextResultItem
): PlaceAddress {
  const a = p?.Address ?? {};
  const line1 =
    [a.AddressNumber, a.Street].filter(Boolean).join(" ").trim() ||
    a.Street ||
    p?.Title ||
    "";
  return {
    placeId: p?.PlaceId ?? "",
    label: a?.Label ?? p?.Title ?? line1,
    line1,
    line2: "",
    city: a.Locality || a.District || "",
    state: a.Region?.Name || a.Region?.Code || "",
    zip: a.PostalCode || "",
  };
}

export type SearchOptions = {
  max?: number;
  circleMeters?: { longitude: number; latitude: number; radius: number };
};

const buildSpatialParams = (opts: SearchOptions): Record<string, unknown> => {
  if (opts.circleMeters) {
    return {
      Filter: {
        Circle: {
          Center: [opts.circleMeters.longitude, opts.circleMeters.latitude],
          Radius: opts.circleMeters.radius,
        },
      },
    };
  }

  return { BiasPosition: { Longitude: 0, Latitude: 0 } };
};

export async function searchAddressesTyped(
  query: string,
  opts: SearchOptions
): Promise<PlaceAddress[]> {
  if (!query || query.length < 2) return [];

  const client = await getPlacesClient();
  const MaxResults = opts.max ?? 8;

  const spatial = buildSpatialParams(opts);

  const [sugRes, autoRes] = await Promise.allSettled([
    client.send(
      new SuggestCommand({
        QueryText: query,
        MaxResults,
        ...spatial,
      })
    ),
    client.send(
      new AutocompleteCommand({
        QueryText: query,
        MaxResults,
        ...spatial,
      })
    ),
  ]);

  const fromSuggest: PlaceAddress[] =
    sugRes.status === "fulfilled"
      ? (sugRes.value.ResultItems ?? []).map(
          (r: SuggestResultItem): PlaceAddress => ({
            placeId: r.Place?.PlaceId ?? "",
            label: r.Place?.Address?.Label ?? r.Title ?? "",
            line1: "",
            line2: "",
            city: "",
            state: "",
            zip: "",
          })
        )
      : [];

  const fromAuto: PlaceAddress[] =
    autoRes.status === "fulfilled"
      ? (autoRes.value.ResultItems ?? []).map(
          (r: AutocompleteResultItem): PlaceAddress => ({
            placeId: r.PlaceId ?? "",
            label: r.Address?.Label ?? r.Title ?? "",
            line1: "",
            line2: "",
            city: "",
            state: "",
            zip: "",
          })
        )
      : [];

  const merged = [...fromSuggest, ...fromAuto].reduce<PlaceAddress[]>(
    (acc, cur) => {
      const i = acc.findIndex((x) => x.label === cur.label);
      if (i === -1) acc.push(cur);
      else if (!acc[i].placeId && cur.placeId) acc[i] = cur;
      return acc;
    },
    []
  );

  return merged.slice(0, MaxResults);
}

export async function resolveSelectedAddress(
  picked: PlaceAddress,
  opts: SearchOptions
): Promise<PlaceAddress | null> {
  const client = await getPlacesClient();

  if (picked.placeId) {
    const gp = await client.send(
      new GetPlaceCommand({ PlaceId: picked.placeId })
    );
    return gp ? mapV2PlaceToAddress(gp) : null;
  }

  const spatial = buildSpatialParams(opts);

  const st = await client.send(
    new SearchTextCommand({
      QueryText: picked.label,
      MaxResults: 1,
      ...spatial,
    })
  );
  const place = st.ResultItems?.[0];
  return place ? mapV2PlaceToAddress(place) : null;
}
